import { FormError, InputRefs } from '@/constant';
import { hasErrorByKey, showAlert } from '@/utils';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikConfig, FormikErrors, FormikValues, useFormik } from 'formik';
import { isNotNilOrEmpty } from 'ramda-adjunct';

export const useFormikCustom = <Values extends FormikValues = FormikValues>(
	formikConfig: FormikConfig<Values> & {
		onValidateError?: (errors: FormikErrors<Values>) => void;
		inputRefs?: React.RefObject<InputRefs<Values>>;
	}
) => {
	const { t } = useTranslation('validation');

	const { onValidateError, inputRefs, ...restFormikConfig } = formikConfig;

	const formik = useFormik(restFormikConfig);

	const latestErrorsRef = useRef<FormikErrors<Values>>({});

	useEffect(() => {
		const validateAndSetErrors = async () => {
			const errors = await formik.validateForm(formik.values);
			latestErrorsRef.current = errors;
		};
		validateAndSetErrors();
	}, [formik.values]);

	const valueKeys = Object.keys(formik.values);

	useEffect(() => {
		if (inputRefs) {
			valueKeys.forEach((name: keyof Values) => {
				if (!inputRefs.current?.[name]) {
					inputRefs.current![name] = React.createRef<HTMLInputElement>();
				}
			});
		}
	}, [valueKeys?.length]);

	const onSubmit = useCallback(async () => {
		await new Promise(requestAnimationFrame);

		const errors = latestErrorsRef.current;

		formik.handleSubmit();

		if (isNotNilOrEmpty(errors)) {
			onValidateError?.(errors);

			if (inputRefs) {
				const firstErrorField = Object.keys(errors)[0] as keyof Values;
				if (firstErrorField && inputRefs?.current?.[firstErrorField]) {
					inputRefs.current[firstErrorField]?.current?.setEditable?.(true);
				}
			}

			switch (true) {
				case hasErrorByKey(errors, FormError.REQUIRED):
					showAlert(t('ALERT_12'));
					break;

				case hasErrorByKey(errors, FormError.FORMAT):
					showAlert(t('ALERT_13'));
					break;

				case hasErrorByKey(errors, FormError.FORMAT_COMMON_INVALID):
					showAlert(t('COMMON_INVALID_REQUEST'));
					break;

				case hasErrorByKey(errors, FormError.IPV4):
					showAlert(t('COMMON_INVALID_IP_ADDRESS'));
					break;

				case hasErrorByKey(errors, FormError.VERIFY_DOMAIN):
					showAlert(t('VERIFY_DOMAIN'));
					break;

				case hasErrorByKey(errors, FormError.DUPLICATE_DOMAIN):
					showAlert(t('DUPLICATE_EMAIL_DOMAIN'));
					break;

				case hasErrorByKey(errors, FormError.VERIFY_EMAIL):
					showAlert(t('ALERT_11'));
					break;

				case hasErrorByKey(errors, FormError.DUPLICATE_EMAIL):
					showAlert(t('DUPLICATE_EMAIL_DOMAIN'));
					break;

				case hasErrorByKey(errors, FormError.PASSWORD_CONFIRM_REQUIRED):
					showAlert(t('ALERT_37'));
					break;

				case hasErrorByKey(errors, FormError.PASSWORD_NOT_MATCHED):
					showAlert(t('ALERT_32'));
					break;

				case hasErrorByKey(errors, FormError.OCCURRENCE_DATE_REQUIRED):
					showAlert(t('WC1_02_SELECT_DATE'));
					break;

				case hasErrorByKey(errors, FormError.ASSIGNMENT_DATE):
					showAlert(t('WC1_02_SELECT_WORKING_DATE'));
					break;

				default:
					break;
			}

			return;
		}
	}, [formik, t]);

	return {
		...formik,
		submitForm: onSubmit,
		onSubmit,
	};
};
